import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import { SharedValue } from 'react-native-reanimated';
import { useSelector } from 'react-redux';

import {
	LockedTournament,
	PlayerGold,
	PlayerGrey,
	TableLogo,
	TableLogoBg,
	TableLogoFr,
	TableLogoru,
} from '../../../assets';
import ChipCoinPng from '../../../assets/chip.png';
import i18n from '../../../i18n/i18n';
import { RootState } from '../../../store';
import { selectIsLogged } from '../../../store/auth/authReducer';
import { TableItem } from '../../../store/types';
import { RootStackParamList } from '../../navigator';
import { CardActionButton } from '../common/CardActionButton';
import { TouchableOpacity } from '../common/Touchable';
import { useTapGuard } from '../common/WheelCarousel/useTapGuard';
import { LobbyCardFrame } from '../Lobby/LobbyCardFrame';
import { TablePlayersBadge } from './TablePlayersBadge';

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const CARD_FONT = 'Sofia Sans Semi Condensed';

const DESIGN_CARD_WIDTH = 226;
const DESIGN_CARD_HEIGHT = 416;

const CHIP_ART = { w: 973, h: 1024, disc: 923, discCenterY: 514.5 / 1024 };
const CHIP_COIN = 15.4;
const TEXT_CENTER_PER_FONT = 1.015 - 0.68 / 2;

const PAD_ASPECT = 186 / 121;
const SEAT_ASPECT = 43 / 30;

const D = {
	title: { x: 12 / 226, y: 15 / 416, w: 202 / 226, h: 45 / 416, font: 36 / 416 },
	minSumY: 67 / 416,
	amountY: 89 / 416,
	amountGapChip: 6.21 / 226,
	sumFont: 18 / 416,
	sumLine: 26 / 18,
	pad: { x: 18 / 226, y: 167 / 416, w: 186 / 226 },
	badge: { x: 61 / 226, y: 297 / 416, w: 98 / 226 },
	button: { x: 22.988 / 226, y: 355 / 416, w: 181.025 / 226 },
	seatW: 43 / 226,
} as const;

const SEAT_CENTERS = [
	{ x: 84, y: 167 },
	{ x: 34.5, y: 182 },
	{ x: 136, y: 167 },
	{ x: 34.5, y: 221 },
	{ x: 185.5, y: 221 },
	{ x: 185.5, y: 182 },
	{ x: 136, y: 236 },
	{ x: 84, y: 236 },
] as const;

const TABLE_IMAGES: Record<string, any> = {
	green: require('../../../assets/tables/green.png'),
	blue: require('../../../assets/tables/blue.png'),
	red: require('../../../assets/tables/red.png'),
	black: require('../../../assets/tables/black.png'),
	vip: require('../../../assets/tables/vip.png'),
	purple: require('../../../assets/tables/purple.png'),
	vegas: require('../../../assets/tables/vegas.png'),
};

const ROOM_PAD_KEYS: Record<string, string> = {
	sofia: 'green',
	london: 'blue',
	paris: 'red',
	monaco: 'black',
	vip: 'vip',
	macao: 'purple',
	vegas: 'vegas',
};

const ROOM_NAME_TRANSLATION_KEYS: Record<string, string> = {
	vip: 'T_SITE_VIP',
	sofia: 'T_SITE_SOFIA',
	london: 'T_SITE_LONDON',
	paris: 'T_SITE_PARIS',
	monaco: 'T_SITE_MONACO',
	macao: 'T_SITE_MACAO',
	vegas: 'T_SITE_VEGAS',
};

const getPadImage = (roomSlug?: string) => {
	const key = ROOM_PAD_KEYS[roomSlug?.trim().toLowerCase() ?? ''] ?? 'green';

	return TABLE_IMAGES[key] ?? TABLE_IMAGES.green;
};

const getTableLogoByLanguage = () => {
	const currentLang = i18n.language || 'en';

	if (currentLang.startsWith('fr')) return TableLogoFr;
	if (currentLang.startsWith('bg')) return TableLogoBg;
	if (currentLang.startsWith('ru')) return TableLogoru;

	return TableLogo;
};

type Nav = NativeStackNavigationProp<RootStackParamList>;

export type TableCardProps = {
	table: TableItem;
	roomSlug?: string;
	index?: number;
	totalCount?: number;
	progress?: SharedValue<number>;
	cardWidth: number;
	cardHeight: number;
	framed?: boolean;
	/**
	 * Set for the VIP room's tables: a logged-in player without VIP gets this (the Become VIP
	 * modal) instead of being sent to a seat the server would refuse.
	 */
	onVipRequired?: () => void;
};

// Memoised: the carousels re-render on every swipe or drag step, and a card only changes with its own props.
export const TableCard = React.memo(function TableCard({
	table,
	roomSlug,
	index = 0,
	totalCount = 0,
	progress,
	cardWidth,
	cardHeight,
	framed = true,
	onVipRequired,
}: TableCardProps) {
	const { t } = useTranslation();
	const { navigate } = useNavigation<Nav>();
	const userId = useSelector((state: RootState) => state.auth.user?.id);
	const isLogged = useSelector(selectIsLogged);
	const isVip = useSelector((state: RootState) => !!state.auth.user?.isVip);

	const width = cardWidth;
	const height = cardHeight;

	const roomKey = roomSlug?.trim().toLowerCase() ?? '';
	const roomDisplayName = ROOM_NAME_TRANSLATION_KEYS[roomKey]
		? t(ROOM_NAME_TRANSLATION_KEYS[roomKey])
		: roomSlug;

	const totalSeats = table?.number_of_seats ?? table?.maxSeats ?? table?.seats?.length ?? 0;
	const ownerId =
		typeof table?.owner === 'object' && table?.owner !== null
			? table.owner.id
			: (table?.owner ?? table?.owner_id);
	const isPrivateTable = !!(table?.isPrivate || table?.is_locked || table?.locked);
	const isOwner = ownerId != null && String(ownerId) === String(userId ?? '');
	const isLocked = isPrivateTable && !isOwner;

	const titleFont = clamp(height * D.title.font, 16, 52);
	const sumFont = clamp(height * D.sumFont, 9, 26);

	const chipHeight = (height / DESIGN_CARD_HEIGHT) * CHIP_COIN * (CHIP_ART.h / CHIP_ART.disc);
	const chipWidth = chipHeight * (CHIP_ART.w / CHIP_ART.h);
	const chipTop =
		height * D.amountY + TEXT_CENTER_PER_FONT * sumFont - chipHeight * CHIP_ART.discCenterY;

	const padWidth = width * D.pad.w;
	const padHeight = padWidth / PAD_ASPECT;
	const padLeft = width * D.pad.x;
	const padTop = height * D.pad.y;
	const seatWidth = width * D.seatW;
	const seatHeight = seatWidth / SEAT_ASPECT;
	const badgeWidth = width * D.badge.w;
	const buttonWidth = width * D.button.w;

	const seats = useMemo(() => {
		const occupied = (table?.seats ?? []).filter(seat => seat.status !== 'EMPTY');
		const occupiedNumbers = new Set<number>(occupied.map(seat => Number(seat.seatNumber)));

		return SEAT_CENTERS.map((center, slot) => ({
			...center,
			busy: occupiedNumbers.size > 0 ? occupiedNumbers.has(slot + 1) : slot < occupied.length,
		}));
	}, [table]);
	const busyCount = useMemo(
		() => (table?.seats ?? []).filter(seat => seat.status !== 'EMPTY').length,
		[table],
	);

	const isWheelMoving = useTapGuard();

	const openTable = useCallback(() => {
		if (!isLogged) {
			navigate('Login');
			return;
		}

		if (onVipRequired && !isVip) {
			onVipRequired();
			return;
		}

		if (isLocked) return;

		navigate('Game', {
			tableId: table?.id,
			legacyRoomId: table?.legacyRoomId,
			roomSlug,
			isTournamentRoom: false,
		});
	}, [
		isLocked,
		isLogged,
		isVip,
		navigate,
		onVipRequired,
		roomSlug,
		table?.id,
		table?.legacyRoomId,
	]);

	const handlePress = useCallback(() => {
		if (isWheelMoving()) return;

		openTable();
	}, [isWheelMoving, openTable]);

	if (width <= 0 || height <= 0) return null;

	return (
		<View style={[styles.wrapper, { width, height }]}>
			{framed ? (
				<LobbyCardFrame
					width={width}
					height={height}
					progress={progress}
					index={index}
					count={totalCount}
				/>
			) : null}

			<TouchableOpacity onPress={handlePress} activeOpacity={0.9} style={styles.fill}>
				<Text
					numberOfLines={1}
					adjustsFontSizeToFit
					minimumFontScale={0.62}
					style={[
						styles.title,
						{
							left: width * D.title.x,
							top: height * D.title.y,
							width: width * D.title.w,
							height: height * D.title.h,
							fontSize: titleFont,
							lineHeight: titleFont * 1.08,
						},
					]}
				>
					# {roomDisplayName} {table?.name}
				</Text>

				<Text
					numberOfLines={1}
					style={[
						styles.minSum,
						{
							top: height * D.minSumY,
							fontSize: sumFont,
							lineHeight: sumFont * D.sumLine,
						},
					]}
				>
					{t('T_SITE_ROOM_MIN_SUM', { defaultValue: 'Min. sum' })}
				</Text>

				<View style={[styles.amountRow, { top: height * D.amountY }]}>
					<Image
						source={ChipCoinPng}
						resizeMode='contain'
						style={{
							marginTop: chipTop - height * D.amountY,
							marginRight: width * D.amountGapChip,
							width: chipWidth,
							height: chipHeight,
						}}
					/>
					<Text
						numberOfLines={1}
						style={[styles.amount, { fontSize: sumFont, lineHeight: sumFont * D.sumLine }]}
					>
						{table?.minimal_chips}
					</Text>
				</View>

				<View
					style={[styles.pad, { left: padLeft, top: padTop, width: padWidth, height: padHeight }]}
				>
					<Image
						source={getPadImage(roomSlug)}
						resizeMode='contain'
						style={{ width: padWidth, height: padHeight }}
					/>
					<Image
						source={getTableLogoByLanguage()}
						resizeMode='contain'
						style={{
							position: 'absolute',
							left: 0,
							top: 0,
							width: padWidth,
							height: padHeight * 0.74,
						}}
					/>
					{isLocked ? (
						<>
							<View style={[styles.padShade, { borderRadius: padHeight / 2 }]} />
							<Image
								source={LockedTournament}
								resizeMode='contain'
								style={{
									position: 'absolute',
									left: (padWidth - padHeight * 0.36) / 2,
									top: padHeight * 0.32,
									width: padHeight * 0.36,
									height: padHeight * 0.36,
								}}
							/>
						</>
					) : null}
				</View>

				{seats.map((seat, slot) => (
					<Image
						key={slot}
						source={seat.busy ? PlayerGold : PlayerGrey}
						resizeMode='contain'
						style={{
							position: 'absolute',
							left: width * (seat.x / DESIGN_CARD_WIDTH) - seatWidth / 2,
							top: height * (seat.y / DESIGN_CARD_HEIGHT) - seatHeight / 2,
							width: seatWidth,
							height: seatHeight,
						}}
					/>
				))}
			</TouchableOpacity>

			<View
				pointerEvents='none'
				style={{
					position: 'absolute',
					left: width * D.badge.x,
					top: height * D.badge.y,
				}}
			>
				<TablePlayersBadge
					title={t('T_SITE_TABLE_PLAYERS', { defaultValue: 'Players' })}
					value={t('T_SITE_TABLE_SEATS_TAKEN', {
						defaultValue: '{{taken}} of {{total}}',
						taken: busyCount,
						total: totalSeats,
					})}
					width={badgeWidth}
				/>
			</View>

			<View
				style={{
					position: 'absolute',
					left: width * D.button.x,
					top: height * D.button.y,
				}}
			>
				<CardActionButton
					label={t('T_SITE_TABLE_JOIN', { defaultValue: 'JOIN TABLE' })}
					width={buttonWidth}
					onPress={handlePress}
					disabled={isLocked}
				/>
			</View>
		</View>
	);
});

const styles = StyleSheet.create({
	wrapper: {
		position: 'relative',
		overflow: 'visible',
	},
	fill: {
		...StyleSheet.absoluteFillObject,
	},
	title: {
		position: 'absolute',
		color: '#FFFFFF',
		fontFamily: CARD_FONT,
		fontWeight: '700',
		textAlign: 'center',
		textTransform: 'uppercase',
		includeFontPadding: false,
		textShadowColor: '#4D0000',
		textShadowOffset: { width: 0, height: 2 },
		textShadowRadius: 1.5,
	},
	minSum: {
		position: 'absolute',
		left: 0,
		right: 0,
		color: '#FFFF99',
		fontFamily: CARD_FONT,
		fontWeight: '700',
		textAlign: 'center',
		includeFontPadding: false,
	},
	amountRow: {
		position: 'absolute',
		left: 0,
		right: 0,
		flexDirection: 'row',
		alignItems: 'flex-start',
		justifyContent: 'center',
	},
	amount: {
		color: '#FFFFFF',
		fontFamily: CARD_FONT,
		fontWeight: '700',
		includeFontPadding: false,
		textShadowColor: '#4D0000',
		textShadowOffset: { width: 0, height: 2 },
		textShadowRadius: 1.5,
	},
	pad: {
		position: 'absolute',
		overflow: 'visible',
	},
	padShade: {
		...StyleSheet.absoluteFillObject,
		backgroundColor: 'rgba(10, 8, 18, 0.42)',
	},
});

export default TableCard;
